import { useOptionalContainer } from "../../player/context.js";
import { useOptionalPopupGroup } from "../../player/popup-group-context.js";
import { useLatestRef } from "../../utils/use-latest-ref.js";
import { useDestroy } from "../../utils/use-destroy.js";
import { TooltipContextProvider } from "./context.js";
import { useSafeId } from "../../utils/use-safe-id.js";
import { useOptionalControlsContext } from "../controls/context.js";
import { usePositionedState } from "../hooks/use-positioned-state.js";
import { useTooltipGroup } from "./group-context.js";
import { TooltipCore, TooltipDataAttrs } from "@videojs/core";
import { createTooltip, createTransition } from "@videojs/core/dom";
import { useSnapshot } from "@videojs/store/react";
import { useEffect, useState } from "react";
import { jsx } from "react/jsx-runtime";
import { isUndefined } from "@videojs/utils/predicate";
//#region src/ui/tooltip/root.tsx
function TooltipRoot({ open: controlledOpen, defaultOpen = TooltipCore.defaultProps.defaultOpen, onOpenChange: onOpenChangeProp, onOpenChangeComplete: onOpenChangeCompleteProp, delay = TooltipCore.defaultProps.delay, closeDelay = TooltipCore.defaultProps.closeDelay, disableHoverablePopup = TooltipCore.defaultProps.disableHoverablePopup, disabled = TooltipCore.defaultProps.disabled, sticky = TooltipCore.defaultProps.sticky, boundary = "container", children, ...coreProps }) {
	const container = useOptionalContainer();
	const popupGroup = useOptionalPopupGroup();
	const controls = useOptionalControlsContext();
	const [core] = useState(() => new TooltipCore(coreProps));
	core.setProps(coreProps);
	const isControlled = !isUndefined(controlledOpen);
	const groupFromContext = useTooltipGroup();
	const onOpenChangeRef = useLatestRef(onOpenChangeProp);
	const onOpenChangeCompleteRef = useLatestRef(onOpenChangeCompleteProp);
	const delayRef = useLatestRef(delay);
	const closeDelayRef = useLatestRef(closeDelay);
	const disableHoverablePopupRef = useLatestRef(disableHoverablePopup);
	const disabledRef = useLatestRef(disabled);
	const stickyRef = useLatestRef(sticky);
	const groupRef = useLatestRef(groupFromContext);
	const popupGroupRef = useLatestRef(popupGroup);
	const [tooltip] = useState(() => {
		const instance = createTooltip({
			transition: createTransition(),
			onOpenChange: (nextOpen, details) => {
				onOpenChangeRef.current?.(nextOpen, details);
			},
			onOpenChangeComplete: (nextOpen) => {
				onOpenChangeCompleteRef.current?.(nextOpen);
			},
			delay: () => delayRef.current,
			closeDelay: () => closeDelayRef.current,
			disableHoverablePopup: () => disableHoverablePopupRef.current,
			disabled: () => disabledRef.current,
			sticky: () => stickyRef.current,
			group: () => groupRef.current,
			popupGroup: () => popupGroupRef.current
		});
		if (!isControlled && defaultOpen) instance.open();
		return instance;
	});
	const [content, setContent] = useState();
	const anchorName = useSafeId();
	const popupId = useSafeId("tooltip");
	useEffect(() => {
		if (isUndefined(controlledOpen)) return;
		const { active: inputOpen } = tooltip.input.current;
		if (controlledOpen === inputOpen) return;
		if (controlledOpen) tooltip.open();
		else tooltip.close();
	}, [controlledOpen, tooltip]);
	useEffect(() => {
		if (isUndefined(controls?.state.visible)) return;
		if (controls.state.visible) return;
		tooltip.close("imperative-action");
	}, [controls?.state.visible, tooltip]);
	useDestroy(tooltip);
	const input = useSnapshot(tooltip.input);
	core.setInput(input);
	const { state, preferredSide, setPositionedSide } = usePositionedState(core.getState());
	return /* @__PURE__ */ jsx(TooltipContextProvider, {
		value: {
			core,
			tooltip,
			state,
			preferredSide,
			setPositionedSide,
			stateAttrMap: TooltipDataAttrs,
			anchorName,
			popupId,
			content,
			setContent,
			boundary,
			container
		},
		children
	});
}
//#endregion
export { TooltipRoot };

//# sourceMappingURL=root.js.map